import React from 'react'
import { mobile, type MobilePastelTone } from './tokens'

export interface MobileStatItem {
  key: string
  label: string
  value: React.ReactNode
  tone?: 'default' | 'blue' | 'green' | 'amber' | 'red'
  /** Pastel card fill — preferred for Zuper-like home summaries */
  fill?: MobilePastelTone
  onClick?: () => void
  /** Selected/active filter state (sets aria-pressed when clickable) */
  pressed?: boolean
}

const valueTone: Record<NonNullable<MobileStatItem['tone']>, string> = {
  default: 'text-gray-900',
  blue: 'text-blue-700',
  green: 'text-emerald-700',
  amber: 'text-amber-700',
  red: 'text-rose-700',
}

const fillValue: Record<MobilePastelTone, string> = {
  rose: 'text-rose-800',
  amber: 'text-amber-800',
  sky: 'text-sky-800',
  emerald: 'text-emerald-800',
  blue: 'text-blue-800',
  teal: 'text-teal-800',
  violet: 'text-violet-800',
  slate: 'text-slate-800',
}

const fillPressed: Record<MobilePastelTone, string> = {
  rose: 'ring-2 ring-rose-400 ring-offset-1 scale-[1.02] shadow-sm border-rose-300',
  amber: 'ring-2 ring-amber-400 ring-offset-1 scale-[1.02] shadow-sm border-amber-300',
  sky: 'ring-2 ring-sky-400 ring-offset-1 scale-[1.02] shadow-sm border-sky-300',
  emerald: 'ring-2 ring-emerald-400 ring-offset-1 scale-[1.02] shadow-sm border-emerald-300',
  blue: 'ring-2 ring-blue-400 ring-offset-1 scale-[1.02] shadow-sm border-blue-300',
  teal: 'ring-2 ring-teal-400 ring-offset-1 scale-[1.02] shadow-sm border-teal-300',
  violet: 'ring-2 ring-violet-400 ring-offset-1 scale-[1.02] shadow-sm border-violet-300',
  slate: 'ring-2 ring-slate-400 ring-offset-1 scale-[1.02] shadow-sm border-slate-300',
}

interface MobileStatStripProps {
  stats: MobileStatItem[]
  className?: string
}

/** Compact 2–4 column stats for phone (pastel tiles when `fill` is set). */
export function MobileStatStrip({ stats, className = '' }: MobileStatStripProps) {
  const cols =
    stats.length <= 2
      ? 'grid-cols-2'
      : stats.length === 3
        ? 'grid-cols-3'
        : 'grid-cols-2'

  return (
    <div className={`grid ${cols} gap-2.5 ${className}`.trim()}>
      {stats.map((s) => {
        const Comp = s.onClick ? 'button' : 'div'
        const filled = s.fill ? mobile.pastel[s.fill] : null
        const pressedClass =
          s.pressed && s.fill
            ? fillPressed[s.fill]
            : s.pressed
              ? 'ring-2 ring-blue-400 ring-offset-1 scale-[1.02] shadow-sm border-blue-300'
              : ''
        return (
          <Comp
            key={s.key}
            type={s.onClick ? 'button' : undefined}
            onClick={s.onClick}
            aria-pressed={s.onClick ? !!s.pressed : undefined}
            className={`rounded-2xl border p-3 text-left transition-transform ${
              filled
                ? filled
                : `${mobile.surface} text-center`
            } ${s.onClick ? 'active:opacity-90' : ''} ${!filled ? 'text-center' : ''} ${pressedClass}`.trim()}
          >
            <p
              className={`text-2xl font-bold tabular-nums leading-none ${
                filled
                  ? fillValue[s.fill!]
                  : valueTone[s.tone || 'default']
              }`}
            >
              {s.value}
            </p>
            <p
              className={`text-[11px] mt-1.5 leading-tight font-medium ${
                filled ? 'opacity-80' : 'text-gray-500'
              }`}
            >
              {s.label}
            </p>
          </Comp>
        )
      })}
    </div>
  )
}
